import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { AppTile } from '@app/models/app-tile';
import { Audit } from '@app/models/audit';
import { AppTileBroadcastMessages } from '@app/models/message-broadcast-app-tile';
import { AuditsService } from '@app/services/audits.service';
import { CacheService } from '@app/services/cache.service';
import { SmartLinksService } from '@app/services/smart-links.service';
import { UserService } from '@app/services/user.service';
import { Environment } from '@environments/interface.environment';
import { isAfter, isBefore } from 'date-fns';
import { BehaviorSubject, concat, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { UserAppTileService } from './user-app-tile.service';

@Injectable({
  providedIn: 'root'
})
export class AppTileService implements AuditsService {

  constructor(private http: HttpClient,
              private cache: CacheService,
              private userService: UserService,
              private userAppTileService: UserAppTileService,
              private smartLinksService: SmartLinksService,
              @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private httpHelper: HttpHelper) { }

  public readonly getAppTilesCacheKey = 'AppTileService-getAppTiles';

  public useCache = true;
  private getAppTiles$: Observable<Array<AppTile>>;

  private reload$ = new Subject<void>();
  public refresh$ = new BehaviorSubject(null);

  public reload() {
    this.reload$.next();
    this.getAppTiles$ = null;
    if (this.useCache) {
      this.cache.removeAllItemsInPersistentCacheWithGeneratedKeyPrefix(this.getAppTilesCacheKey);
    }
    this.userAppTileService.reload();
    this.smartLinksService.reload();
    this.refresh$.next(null);
  }

  /*
   * Returns app tiles that the current user is licensed to use
   * If keepUnlicensed is set to true, ALL app are returned -> instead property UserIsLicensed is populated
   */
  getLicensedAppTiles(keepUnlicensed = false): Observable<Array<AppTile>> {
    const appTiles$ = this.getAppTiles();
    const userDetails$ = this.userService.getMyDetailsFromMsGraph();

    return forkJoin([appTiles$, userDetails$])
      .pipe(
        map(([appTiles, userDetails]) => {
          // remove apps that the user does not have a license to user
          if (!keepUnlicensed) {
            appTiles = appTiles.filter(app => {
              if (!app.O365ServiceName || app.O365ServiceName === '') { return true; }  // no license required
              const foundLicense = userDetails.assignedPlans.find(plan => {
                return plan.service.toLowerCase() === app.O365ServiceName.toLowerCase();
              });
              return foundLicense !== undefined && foundLicense.capabilityStatus === 'Enabled';
            });
          }

          appTiles.map(app => {
            if (!app.O365ServiceName || app.O365ServiceName === '') {
              app.UserIsLicensed = true;
            } else {
              const foundLicense = userDetails.assignedPlans.find(plan => {
                return plan.service.toLowerCase() === app.O365ServiceName.toLowerCase();
              });
              app.UserIsLicensed = foundLicense !== undefined && foundLicense.capabilityStatus === 'Enabled';
            }
          });
          return appTiles;
        })
      );
  }

  getAppTiles(): Observable<Array<AppTile>> {
    if (this.getAppTiles$) {
      return this.getAppTiles$;
    } else {
      this.getAppTiles$ = this.http.get<Array<AppTile>>(`${Constants.API_ENDPOINT}/apptiles`)
        .pipe(
          map(response => {
            console.log(`getAppTiles: `, response);
            return response.map(p => new AppTile(p));
          }),
          tap(data => {
            this.useCache && this.cache.set(this.getAppTilesCacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getAppTiles', result: new Array<AppTile>(), rethrow: true, cacheKey: this.getAppTilesCacheKey })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<AppTile>>(this.getAppTilesCacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getAppTiles$);
        }
      }
      return this.getAppTiles$;
    }
  }

  getAppTile(id: number): Observable<AppTile> {
    return this.http.get<AppTile>(`${Constants.API_ENDPOINT}/apptiles/${id}`)
      .pipe(
        map(response => {
          console.log(`getAppTile: `, response);
          const appTile = response as AppTile;
          // filter AppTileBroadcastMessages to get only active messages on app tile.
          const activeAptileBroadcastMessages: AppTileBroadcastMessages[] = appTile.AppTileBroadcastMessages.filter(b => (!b.BroadcastMessage.EndDate || isAfter(new Date(b.BroadcastMessage.EndDate), new Date())) && (!b.BroadcastMessage.StartDate || isBefore(new Date(b.BroadcastMessage.StartDate), new Date())));
          appTile.AppTileBroadcastMessages = activeAptileBroadcastMessages;

          return new AppTile(appTile);
        }),
        catchError(this.httpHelper.handleError({ operation: 'getAppTile', result: null, rethrow: true }))
      );
  }

  updateAppTile(id: number, appTile: AppTile): Observable<AppTile> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.put<AppTile>(`${Constants.API_ENDPOINT}/apptiles/${id}`, appTile, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`updateAppTile: `, response);
          this.reload();
          return appTile;
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateAppTile', result: null, rethrow: true })));

    return fetchOperation;
  }

  deleteAppTile(id: number): Observable<AppTile> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.delete<AppTile>(`${Constants.API_ENDPOINT}/apptiles/${id}`, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`deleteAppTile `, response);
          this.reload();
          return response;
        }),
        catchError(this.httpHelper.handleError({ operation: 'deleteAppTile', result: null, rethrow: true })));

    return fetchOperation;
  }

  addAppTile(userAppTile: AppTile): Observable<AppTile> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.post<AppTile>(`${Constants.API_ENDPOINT}/apptiles`, userAppTile, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`addAppTile: `, response);
          this.reload();
          return new AppTile(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'addAppTile', result: null, rethrow: true })));

    return fetchOperation;
  }

  getUnreadCounterFromEndpoint(appTile: AppTile): Observable<number> {
    return this.http.get<any>(`${appTile.IntegrationEndpointUrl}`).
      pipe(
        map(response => {
          let result = null;
          if (appTile.IntegrationEndpointUrl.startsWith('https://graph.microsoft.com/v1.0/me/mailFolders')) {
            result = response['unreadItemCount'] as number;
          } else {
            if (appTile.IntegrationEndpointUrl.startsWith('https://graph.microsoft.com')) {
              result = response['@odata.count'] as number;
            } else if (appTile.IntegrationEndpointUrl.indexOf('/api/me/yammer/notifications') !== -1) {
              if (response && response.length) {
                result = response[0].unseen_notification_count + response[0].unseen_message_count;
              }
            } else if (Array.isArray(response)) {
              result = (response as Array<any>).length;
            }
          }

          if (result && result > 0) {
            return result;
          }
          return null;
        }));
  }

  getAudits(id: number): Observable<Array<Audit>> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http.
      get<Array<Audit>>(`${Constants.API_ENDPOINT}/apptiles/${id}/audits`, {
        params: httpParams
      })
      .pipe(
        map(results => {
          return results.map(r => new Audit(r));
        }),
        catchError(this.httpHelper.handleError({ operation: 'getAudits', result: null, rethrow: true }))
      );
  }

  restoreToAudit<AppTile>(id: number, auditId: number): Observable<AppTile> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http.post<AppTile>(`${Constants.API_ENDPOINT}/apptiles/${id}/audits/${auditId}/restore`, {}, {
      params: httpParams
    })
      .pipe(
        tap(() => {
          this.reload();
        }),
        catchError(this.httpHelper.handleError({ operation: 'restoreToAudit', result: null, rethrow: true })));
  }

  searchAppTile(keyword: string, max: number): Observable<Array<AppTile>> {

    // abort if no keyword provided
    if (!keyword) {
      return of([]);
    }

    const cacheKey = `${this.getAppTilesCacheKey}-searchForApps-${keyword}-${max}`;
    const cachedData = this.cache.get<Array<AppTile>>(cacheKey);
    if (cachedData) {
      return of(cachedData);
    }

    const httpParams = new HttpParams()
      .append('key', keyword.toLowerCase())
      .append('max', max.toString());

    const fetchOperation = this.http.get<Array<AppTile>>(`${Constants.API_ENDPOINT}/apptiles/suggestions`, {
      params: httpParams
    })
      .pipe(
        tap(result => {
          this.useCache && this.cache.set(cacheKey, result);
        }),
        catchError(this.httpHelper.handleError({ operation: 'searchAppTile', result: new Array<AppTile>(), rethrow: true, cacheKey: cacheKey }))
      );
    return fetchOperation;
  }
}
